import type { NotificationViewModel } from '../config/notification-presenters';
import { NOTIFICATION_TONE_CLASSES } from '../config/notification-tone';

type NotificationItemProps = {
  notification: NotificationViewModel;
  onSelect: (_notification: NotificationViewModel) => void;
};

/**
 * Uma linha do painel do sino.
 *
 * Apresentacional: recebe o view-model pronto (ícone, tom, datas já
 * formatadas) e não sabe o que é apólice, emissão ou cobrança.
 */
export function NotificationItem({
  notification,
  onSelect,
}: NotificationItemProps) {
  const Icon = notification.icon;
  const tone = NOTIFICATION_TONE_CLASSES[notification.tone];

  return (
    <button
      type='button'
      onClick={() => onSelect(notification)}
      /*
       * O hover de cada estado é uma versão mais forte da PRÓPRIA cor de fundo.
       * Um cinza único não servia: sobre o azul da não lida ele quase não muda
       * nada, e a linha parecia não reagir ao mouse.
       *
       * `cursor-pointer` explícito porque o reset do Tailwind deixa `button` com
       * `cursor: default` — é a convenção do resto do portal.
       */
      className={`w-full cursor-pointer px-3 py-3 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-alba-blue-400 ${
        notification.read
          ? 'hover:bg-alba-gray-100'
          : 'bg-alba-blue-50/40 hover:bg-alba-blue-100'
      }`}
    >
      <div className='flex items-start gap-2'>
        <span
          className={`flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full ${tone.iconWrapper}`}
        >
          <Icon className='h-3.5 w-3.5' strokeWidth={1.8} />
        </span>
        <div className='flex min-w-0 flex-1 flex-col'>
          <div className='flex items-start justify-between gap-2'>
            <span className='text-sm font-semibold text-alba-black-500'>
              {notification.title}
            </span>
            {!notification.read && (
              <span
                className='mt-1.5 h-2 w-2 flex-shrink-0 rounded-full bg-alba-blue-600'
                aria-label='Não lida'
              />
            )}
          </div>
          <span className='text-xs text-alba-gray-700'>
            {notification.description}
          </span>
          <span className='mt-1 text-[11px] text-alba-gray-600'>
            {notification.createdAtLabel}
          </span>
        </div>
      </div>
    </button>
  );
}
